import { Button } from "@/shared/components/Button";

import { useEffect } from "react";

type Props = {
  isOpen: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel?: string;
  showCancelButton?: boolean;
  isSubmitting?: boolean;
  onConfirm: () => void;
  onCancel?: () => void;
};

export function ConfirmDialog({
  isOpen,
  title,
  body,
  confirmLabel,
  cancelLabel,
  showCancelButton = true,
  isSubmitting = false,
  onConfirm,
  onCancel,
}: Props) {
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        if (showCancelButton && onCancel) {
          onCancel();
        }
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onCancel, showCancelButton]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="confirm-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-10"
    >
      <div className="w-full max-w-md rounded-2xl border border-[color:var(--border-light)] bg-[color:var(--surface)] p-6 shadow-[var(--shadow-lg)]">
        <h3 className="text-base font-semibold text-[color:var(--text-primary)]">
          {title}
        </h3>
        <p className="mt-2 text-sm text-[color:var(--text-muted)]">{body}</p>
        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:justify-end">
          <Button
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting}
            data-testid="confirm-dialog-confirm"
            variant="danger" className="h-11 rounded-lg px-4 text-sm font-semibold text-white shadow-sm transition disabled:opacity-60"
          >
            {confirmLabel}
          </Button>
          {showCancelButton ? (
            <Button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              data-testid="confirm-dialog-cancel"
              variant="primary" className="h-11 rounded-lg px-4 text-sm font-semibold shadow-sm transition disabled:opacity-60"
            >
              {cancelLabel}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
